import React, { useCallback, useEffect, useMemo, useState, useRef, Component } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MenuItemCard } from '../components/restaurant/MenuItemCard';
import { ProductDetailModal } from '../components/restaurant/ProductDetailModal';
import { CartItem as CartItemComponent } from '../components/restaurant/CartItem';
import { useRestaurantBySlug, useMenuData } from '../lib/hooks';
import { useCart } from '../lib/cart';
import { supabase } from '../lib/supabase';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { Store, ShoppingBag, X, MessageCircle, ArrowLeft, CheckCircle2, MapPin, Bike, Flame, Plus } from 'lucide-react';
import { MenuItem, CartItemOption } from '../lib/types';
import { formatCurrency, cn } from '../lib/utils';
import { buildWhatsAppMessage, buildWhatsAppUrl } from '../lib/whatsapp';
import { getSubdomainSlug } from '../lib/subdomain';
import { useKitchenPause } from '../lib/kitchen-pause-context';
import { isPreorderRestaurant, DELIVERY_TIME_SLOTS, getUpcomingSaturdays, getUpcomingDeliveryDates, CAPRICHO_DELIVERY_HOURS, formatDeliveryDate, toDateString, getOrderingWindowMessage, checkPreorderCapacity, CAPACITY_FULL_MESSAGE, fetchDeliveryTimeSlots, checkTimeSlotsAvailability, type TimeSlotWithAvailability } from '../lib/preorder';
// Keep emoji helper for upsell section labels
function getCategoryEmoji(name: string): string {
  const emojiMap: Record<string, string> = {
    bebida: '🥤',
    postre: '🍮',
    taco: '🌮',
    burrito: '🌯',
    combo: '🔥',
    entrada: '🍽️',
    ensalada: '🥗',
    nacho: '🧀',
    sopa: '🍲',
    pizza: '🍕',
    hamburguesa: '🍔',
    extra: '✨',
    especial: '⭐'
  };
  const lower = name.toLowerCase().trim();
  for (const [key, emoji] of Object.entries(emojiMap)) {
    if (lower.includes(key)) return emoji;
  }
  return '🔥';
}
const SECTION_TITLES: Record<string, string> = {
  tacos: '🌮 Tacos que no fallan',
  burritos: '🌯 Burritos bien cargados',
  quesadillas: '🧀 Quesadillas irresistibles',
  bowls: '🥗 Bowls con power',
  antojitos: '🔥 Antojitos para compartir',
  hamburguesas: '🍔 Hamburguesas de verdad',
  pizzas: '🍕 Pizzas artesanales',
  combos: '💣 Combos para romper dieta',
  bebidas: '🥤 Para bajar el fuego',
  postres: '🍮 El toque dulce',
  entradas: '🍽️ Para abrir boca',
  ensaladas: '🥗 Algo más fresquito',
  extras: '✨ Extras que suman',
  especiales: '⭐ Especiales de la casa',
  nachos: '🧀 Nachos cargados',
  sopas: '🍲 Calditos reconfortantes'
};
function getCreativeTitle(name: string): string {
  const lower = name.toLowerCase().trim();
  for (const [key, title] of Object.entries(SECTION_TITLES)) {
    if (lower.includes(key)) return title;
  }
  return `🔥 ${name}`;
}
// Check if a category is an upsell category (bebidas/postres)
function isUpsellCategory(name: string): boolean {
  const lower = name.toLowerCase().trim();
  return ['bebida', 'bebidas', 'drinks', 'postre', 'postres', 'desserts', 'refresco', 'refrescos'].some(k => lower.includes(k));
}
interface PublicMenuPageProps {
  /** Optional slug from subdomain — overrides useParams */
  subdomainSlug?: string;
}
export function PublicMenuPage({
  subdomainSlug
}: PublicMenuPageProps = {}) {
  const {
    slug: pathSlug
  } = useParams<{
    slug: string;
  }>();
  // Priority: explicit subdomain prop > auto-detected subdomain > path param
  const slug = subdomainSlug || getSubdomainSlug() || pathSlug;
  const navigate = useNavigate();
  const {
    restaurant,
    settings: restaurantSettings,
    loading: restaurantLoading
  } = useRestaurantBySlug(slug);
  const {
    isKitchenPaused,
    monitorRestaurant
  } = useKitchenPause();
  const {
    categories: rawCategories,
    items: rawItems,
    loading: menuLoading
  } = useMenuData(restaurant?.id);
  // Auto-generate categories from subcategory field when menu_categories is empty
  // This handles restaurants like Capricho de Canela that use subcategory instead
  const {
    categories,
    items
  } = useMemo(() => {
    if (rawCategories.length > 0) {
      return {
        categories: rawCategories,
        items: rawItems
      };
    }
    if (rawItems.length === 0) {
      return {
        categories: rawCategories,
        items: rawItems
      };
    }
    // Build synthetic categories from unique subcategory values
    const subcategoryMap = new Map<string, {
      minSort: number;
      items: typeof rawItems;
    }>();
    for (const item of rawItems) {
      const sub = item.subcategory || 'Menú';
      if (!subcategoryMap.has(sub)) {
        subcategoryMap.set(sub, {
          minSort: item.sort_order,
          items: []
        });
      }
      subcategoryMap.get(sub)!.items.push(item);
      subcategoryMap.get(sub)!.minSort = Math.min(subcategoryMap.get(sub)!.minSort, item.sort_order);
    }
    // Sort subcategories by their earliest item sort_order
    const sortedSubs = [...subcategoryMap.entries()].sort((a, b) => a[1].minSort - b[1].minSort);
    const syntheticCategories = sortedSubs.map(([sub, data], idx) => ({
      id: `sub-${sub}`,
      restaurant_id: restaurant?.id || '',
      name: sub,
      slug: sub.toLowerCase().replace(/\s+/g, '-'),
      description: null,
      image_url: null,
      sort_order: idx,
      is_active: true,
      created_at: '',
      updated_at: ''
    }));
    // Remap items so category_id matches the synthetic category id
    const remappedItems = rawItems.map(item => ({
      ...item,
      category_id: `sub-${item.subcategory || 'Menú'}`
    }));
    return {
      categories: syntheticCategories,
      items: remappedItems
    };
  }, [rawCategories, rawItems, restaurant?.id]);
  const {
    cart,
    addItem,
    updateQuantity,
    removeItem,
    getTotal,
    getItemCount,
    clearCart
  } = useCart();
  // Register restaurant with central kitchen pause monitor
  useEffect(() => {
    if (restaurant?.id) {
      console.log('[NOIZA][PublicMenuPage] Registering restaurant:', restaurant.id, 'slug:', slug);
      monitorRestaurant(restaurant.id);
    }
  }, [restaurant?.id, monitorRestaurant]);
  const [activeCategory, setActiveCategory] = useState<string>('');
  const [selectedProduct, setSelectedProduct] = useState<MenuItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [showUpsellModal, setShowUpsellModal] = useState(false);
  const [animKey, setAnimKey] = useState(0); // for triggering re-animation
  // Checkout state
  const [cartStep, setCartStep] = useState<'review' | 'checkout' | 'success'>('review');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [deliveryType, setDeliveryType] = useState<'pickup' | 'delivery'>('delivery');
  const [orderNotes, setOrderNotes] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryLat, setDeliveryLat] = useState<number | null>(null);
  const [deliveryLng, setDeliveryLng] = useState<number | null>(null);
  const [mapsUrl, setMapsUrl] = useState<string | null>(null);
  const [isLocating, setIsLocating] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [completedOrder, setCompletedOrder] = useState<{
    orderNumber: string;
    trackingToken: string;
    whatsappUrl: string;
  } | null>(null);
  // Preorder state (Capricho de Canela)
  const [preorderDate, setPreorderDate] = useState('');
  const [preorderTimeSlot, setPreorderTimeSlot] = useState('');
  const [preorderCapacityFull, setPreorderCapacityFull] = useState(false);
  const [checkingCapacity, setCheckingCapacity] = useState(false);
  const [dynamicTimeSlots, setDynamicTimeSlots] = useState<TimeSlotWithAvailability[]>([]);
  const [timeSlotsLoading, setTimeSlotsLoading] = useState(false);
  const [deliveryHour, setDeliveryHour] = useState('');
  const [showCaprichoWelcome, setShowCaprichoWelcome] = useState(false);
  const isCapricho = String(restaurant?.id) === '2' || restaurant?.slug === 'capricho-de-canela';
  // Check preorder capacity + load time slots when date is selected
  const handlePreorderDateSelect = async (dateStr: string) => {
    setPreorderDate(dateStr);
    setPreorderTimeSlot('');
    setPreorderCapacityFull(false);
    setDynamicTimeSlots([]);
    if (restaurant?.id && isPreorderRestaurant(restaurant.id)) {
      setCheckingCapacity(true);
      setTimeSlotsLoading(true);
      // Check overall Saturday capacity
      const capacityResult = await checkPreorderCapacity(restaurant.id, dateStr);
      setPreorderCapacityFull(!capacityResult.available);
      // Load time slots with per-slot availability
      if (capacityResult.available) {
        const slots = await fetchDeliveryTimeSlots(restaurant.id);
        const slotsWithAvailability = await checkTimeSlotsAvailability(restaurant.id, dateStr, slots);
        setDynamicTimeSlots(slotsWithAvailability);
      }
      setCheckingCapacity(false);
      setTimeSlotsLoading(false);
    }
  };
  // Show Capricho welcome modal on first visit
  useEffect(() => {
    if (isCapricho && restaurant) {
      const dismissed = sessionStorage.getItem('capricho-welcome-dismissed');
      if (!dismissed) {
        setShowCaprichoWelcome(true);
      }
    }
  }, [isCapricho, restaurant]);
  // Set default delivery type based on restaurant_settings
  useEffect(() => {
    if (restaurantSettings) {
      if (restaurantSettings.accept_delivery) {
        setDeliveryType('delivery');
      } else if (restaurantSettings.accept_pickup) {
        setDeliveryType('pickup');
      }
    }
  }, [restaurantSettings]);
  // Set initial active category
  useEffect(() => {
    if (categories.length > 0 && !activeCategory) {
      setActiveCategory(categories[0].id);
    }
  }, [categories, activeCategory]);
  // Reset cart step when cart opens/closes
  useEffect(() => {
    if (!isCartOpen) {
      setCartStep('review');
      setSubmitError(null);
    }
  }, [isCartOpen]);
  const handleCategorySelect = (categoryId: string) => {
    setActiveCategory(categoryId);
    // Scroll to specific category section
    const el = document.getElementById(`category-${categoryId}`);
    if (el) {
      // Offset for sticky headers/nav
      const y = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
    }
  };
  // Auto-update active category based on scroll position (lightweight observer)
  useEffect(() => {
    if (categories.length === 0) return;
    const observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          const id = entry.target.id.replace('category-', '');
          setActiveCategory(id);
          break;
        }
      }
    }, {
      rootMargin: '-80px 0px -70% 0px',
      threshold: 0
    });
    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      categories.forEach(cat => {
        const el = document.getElementById(`category-${cat.id}`);
        if (el) observer.observe(el);
      });
    }, 100);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [categories]);
  const handleProductClick = (item: MenuItem) => {
    setSelectedProduct(item);
    setIsModalOpen(true);
  };
  const handleAddToCart = (item: MenuItem, quantity: number, options: CartItemOption[], notes: string) => {
    if (!restaurant) return;
    if (isKitchenPaused) return;
    const success = addItem({
      menu_item_id: item.id,
      restaurant_id: restaurant.id,
      item_name_snapshot: item.name,
      unit_price_snapshot: item.price,
      quantity,
      notes,
      image_url: item.image_url,
      selected_options: options
    }, restaurant.name);
    if (!success) {
      alert('Ya tienes productos de otro restaurante en tu carrito. Por favor, finaliza o vacía tu pedido actual primero.');
    } else {
      setIsCartOpen(true);
    }
  };
  // Quick add for upsell (no options, quantity 1)
  const handleQuickAdd = (item: MenuItem) => {
    if (!restaurant) return;
    addItem({
      menu_item_id: item.id,
      restaurant_id: restaurant.id,
      item_name_snapshot: item.name,
      unit_price_snapshot: item.price,
      quantity: 1,
      notes: '',
      image_url: item.image_url,
      selected_options: []
    }, restaurant.name);
  };
  // Intercept checkout: show upsell first
  const handleProceedToCheckout = () => {
    // Check if there are upsell items available
    const upsellItems = getUpsellItems();
    if (upsellItems.length > 0) {
      setShowUpsellModal(true);
    } else {
      setCartStep('checkout');
    }
  };
  // Get upsell items (bebidas + postres)
  const getUpsellItems = () => {
    const upsellCats = categories.filter(c => isUpsellCategory(c.name));
    return items.filter(item => upsellCats.some(c => c.id === item.category_id));
  };
  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Tu navegador no soporta geolocalización. Ingresa tu dirección manualmente.');
      return;
    }
    setIsLocating(true);
    setLocationError(null);
    navigator.geolocation.getCurrentPosition(position => {
      const lat = position.coords.latitude;
      const lng = position.coords.longitude;
      setDeliveryLat(lat);
      setDeliveryLng(lng);
      setMapsUrl(`https://www.google.com/maps?q=${lat},${lng}`);
      setIsLocating(false);
      setLocationError(null);
      console.log('[NOIZA][public] Location captured:', {
        lat,
        lng
      });
    }, error => {
      setIsLocating(false);
      console.error('[NOIZA][public] Geolocation error:', error);
      switch (error.code) {
        case error.PERMISSION_DENIED:
          setLocationError('Permiso de ubicación denegado. Ingresa tu dirección manualmente.');
          break;
        case error.POSITION_UNAVAILABLE:
          setLocationError('No se pudo obtener tu ubicación. Ingresa tu dirección manualmente.');
          break;
        default:
          setLocationError('Error al obtener ubicación. Ingresa tu dirección manualmente.');
      }
    }, {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0
    });
  };
  const handleSubmitOrder = async () => {
    if (!restaurant || !cart.restaurantId) return;
    // Frontend guard
    if (isKitchenPaused) {
      setSubmitError('La cocina está pausada. No podemos recibir pedidos. Vuelve en 10 minutos.');
      return;
    }
    // Backend guard — re-check DB right before creating order
    const {
      checkKitchenPausedBeforeOrder: checkPause
    } = await import('../lib/kitchen-pause-context');
    const pauseCheck = await checkPause(cart.restaurantId);
    if (!pauseCheck.allowed) {
      setSubmitError(pauseCheck.error || 'La cocina está pausada.');
      return;
    }
    if (!customerName.trim() || !customerPhone.trim()) {
      setSubmitError('Por favor ingresa tu nombre y teléfono.');
      return;
    }
    // Preorder validation
    if (isPreorderRestaurant(restaurant.id)) {
      if (!preorderDate) {
        setSubmitError('Selecciona una fecha de entrega (sábado).');
        return;
      }
      if (!preorderTimeSlot) {
        setSubmitError('Selecciona un horario de entrega.');
        return;
      }
      // Backend guard — re-check overall Saturday capacity
      const capacityCheck = await checkPreorderCapacity(restaurant.id, preorderDate);
      if (!capacityCheck.available) {
        setPreorderCapacityFull(true);
        setSubmitError(CAPACITY_FULL_MESSAGE);
        return;
      }
      // Backend guard — re-check specific time slot capacity
      const slots = await fetchDeliveryTimeSlots(restaurant.id);
      const slotsAvail = await checkTimeSlotsAvailability(restaurant.id, preorderDate, slots);
      const selectedSlot = slotsAvail.find(s => s.label === preorderTimeSlot);
      if (selectedSlot && !selectedSlot.available) {
        setDynamicTimeSlots(slotsAvail);
        setPreorderTimeSlot('');
        setSubmitError('El horario seleccionado ya no está disponible. Por favor elige otro.');
        return;
      }
    }
    if (deliveryType === 'delivery' && !mapsUrl && !deliveryAddress.trim()) {
      setSubmitError('Para delivery, comparte tu ubicación o ingresa tu dirección.');
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const subtotal = getTotal();
      const deliveryFee = deliveryType === 'delivery' ? restaurant.express_fee || 0 : 0;
      const orderTotal = subtotal + deliveryFee;
      const trackingToken = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
      const orderPayload = {
        restaurant_id: cart.restaurantId,
        tracking_token: trackingToken,
        customer_name: customerName.trim(),
        customer_phone: customerPhone.trim(),
        delivery_type: deliveryType,
        payment_method: 'cash',
        delivery_address: deliveryType === 'delivery' ? deliveryAddress.trim() || null : null,
        delivery_lat: deliveryType === 'delivery' ? deliveryLat : null,
        delivery_lng: deliveryType === 'delivery' ? deliveryLng : null,
        maps_url: deliveryType === 'delivery' ? mapsUrl : null,
        table_id: null,
        delivery_date: isPreorderRestaurant(restaurant.id) && preorderDate ? preorderDate : null,
        delivery_time_slot: isPreorderRestaurant(restaurant.id) && preorderTimeSlot ? preorderTimeSlot : null,
        notes: (() => {
          let n = orderNotes.trim();
          if (isPreorderRestaurant(restaurant.id) && preorderDate && preorderTimeSlot) {
            const preorderInfo = `📦 PREORDER — Entrega: ${preorderDate} (sábado) · Horario: ${preorderTimeSlot}`;
            n = n ? `${preorderInfo}\n${n}` : preorderInfo;
          }
          return n || null;
        })(),
        subtotal,
        delivery_fee: deliveryFee,
        total: orderTotal,
        status: 'new',
        source: isPreorderRestaurant(restaurant.id) ? 'preorder_web' : 'public_web'
      };
      console.log('[NOIZA][public] Creating order:', JSON.stringify(orderPayload, null, 2));
      const {
        data: order,
        error: orderError
      } = await supabase.from('orders').insert(orderPayload).select().single();
      if (orderError) {
        console.error('[NOIZA][public] Order insert error:', orderError);
        throw new Error(`Error al crear pedido: ${orderError.message}`);
      }
      console.log('[NOIZA][public] Order created:', order);
      for (const item of cart.items) {
        const optionsTotal = item.selected_options.reduce((sum, opt) => sum + opt.price_delta, 0);
        const lineTotal = (item.unit_price_snapshot + optionsTotal) * item.quantity;
        const itemPayload = {
          order_id: order.id,
          restaurant_id: cart.restaurantId,
          menu_item_id: item.menu_item_id,
          item_name_snapshot: item.item_name_snapshot,
          unit_price_snapshot: item.unit_price_snapshot,
          quantity: item.quantity,
          notes: item.notes || null,
          station_code: null,
          line_total: lineTotal,
          status: 'pending'
        };
        console.log('[NOIZA][public] Creating order item:', JSON.stringify(itemPayload, null, 2));
        const {
          data: orderItem,
          error: itemError
        } = await supabase.from('order_items').insert(itemPayload).select().single();
        if (itemError) {
          console.error('[NOIZA][public] Order item insert error:', itemError, {
            item: item.item_name_snapshot
          });
          throw new Error(`Error al guardar producto: ${itemError.message}`);
        }
        console.log('[NOIZA][public] Order item created:', orderItem);
        if (item.selected_options.length > 0) {
          const optionsToInsert = item.selected_options.map(opt => ({
            order_item_id: orderItem.id,
            restaurant_id: cart.restaurantId,
            option_group: opt.option_group || '',
            option_name: opt.option_name,
            price_delta: opt.price_delta
          }));
          console.log('[NOIZA][public] Creating order item options:', JSON.stringify(optionsToInsert, null, 2));
          const {
            error: optionsError
          } = await supabase.from('order_item_options').insert(optionsToInsert);
          if (optionsError) {
            console.error('[NOIZA][public] Order item options insert error:', optionsError);
          }
        }
      }
      const {
        error: trackingError
      } = await supabase.from('customer_tracking_events').insert({
        restaurant_id: cart.restaurantId,
        order_id: order.id,
        status: 'new',
        title: 'Pedido recibido',
        message: 'Tu pedido fue recibido correctamente y está esperando confirmación del restaurante.',
        visible_to_customer: true
      });
      if (trackingError) {
        console.error('[NOIZA][public] Tracking event insert error:', trackingError);
      }
      const {
        error: logError
      } = await supabase.from('order_logs').insert({
        restaurant_id: cart.restaurantId,
        order_id: order.id,
        event_type: 'order_created',
        event_payload: {
          source: 'public_web',
          delivery_type: deliveryType
        }
      });
      if (logError) {
        console.error('[NOIZA][public] Order log insert error:', logError);
      }
      const itemsSnapshot = [...cart.items];
      let ticketImageUrl: string | null = null;
      try {
        console.log('[NOIZA][public] Ticket generation started...');
        const {
          generateTicketImage,
          uploadTicketToCloudinary
        } = await import('../lib/ticket');
        const ticketBlob = await generateTicketImage({
          restaurantName: restaurant.name,
          currencySymbol: restaurant.currency_symbol || '₡',
          orderNumber: order.order_number,
          customerName: customerName.trim(),
          customerPhone: customerPhone.trim(),
          deliveryType,
          deliveryAddress: deliveryType === 'delivery' ? deliveryAddress.trim() || null : null,
          mapsUrl: deliveryType === 'delivery' ? mapsUrl : null,
          items: itemsSnapshot,
          subtotal,
          deliveryFee,
          total: orderTotal,
          notes: orderNotes.trim() || null,
          createdAt: order.created_at || new Date().toISOString()
        });
        console.log('[NOIZA][public] Ticket blob generated, size:', ticketBlob.size);
        const url = await uploadTicketToCloudinary(ticketBlob, order.order_number);
        if (url) {
          ticketImageUrl = url;
          console.log('[NOIZA][public] Ticket uploaded:', ticketImageUrl);
          const {
            error: ticketUpdateError
          } = await supabase.from('orders').update({
            ticket_image_url: ticketImageUrl
          }).eq('id', order.id);
          if (ticketUpdateError) {
            console.error('[NOIZA][public] Ticket URL update error:', ticketUpdateError);
          } else {
            console.log('[NOIZA][public] Ticket URL saved to order row');
          }
        } else {
          console.warn('[NOIZA][public] Ticket upload returned null URL');
        }
      } catch (ticketErr) {
        console.error('[NOIZA][public] Ticket generation/upload failed:', ticketErr);
      }
      console.log('[NOIZA][public] Final ticket_image_url:', ticketImageUrl);
      const whatsappMessage = buildWhatsAppMessage({
        restaurantName: restaurant.name,
        whatsappNumber: restaurant.whatsapp_number || '',
        currencySymbol: restaurant.currency_symbol || '₡',
        orderNumber: order.order_number,
        customerName: customerName.trim(),
        customerPhone: customerPhone.trim(),
        deliveryType,
        paymentMethod: 'cash',
        items: itemsSnapshot,
        subtotal,
        deliveryFee,
        total: orderTotal,
        deliveryAddress: deliveryType === 'delivery' ? deliveryAddress.trim() || null : null,
        mapsUrl: deliveryType === 'delivery' ? mapsUrl : null,
        notes: orderNotes.trim() || null,
        ticketImageUrl,
        trackingToken
      });
      console.log('[NOIZA][public] Final WhatsApp message payload:', {
        orderNumber: order.order_number,
        ticketImageUrl,
        trackingToken,
        itemCount: itemsSnapshot.length
      });
      const whatsappUrl = restaurant.whatsapp_number ? buildWhatsAppUrl(restaurant.whatsapp_number, whatsappMessage) : '';
      console.log('[NOIZA][public] WhatsApp URL:', whatsappUrl);
      setCompletedOrder({
        orderNumber: order.order_number,
        trackingToken,
        whatsappUrl
      });
      setCartStep('success');
      if (whatsappUrl) {
        window.open(whatsappUrl, '_blank');
      }
      clearCart();
    } catch (err: any) {
      console.error('[NOIZA][public] Order submission error:', err);
      setSubmitError(err?.message || 'Error al procesar tu pedido. Intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };
  // ─── LOADING ───
  if (restaurantLoading || menuLoading) {
    return <div className="min-h-screen flex flex-col bg-[#0B0B0B]" data-id="element-2205">
        <div className="h-[60vh] sm:h-[50vh] w-full bg-[#141414] animate-pulse" data-id="element-2206" />
        <div className="max-w-4xl mx-auto px-4 w-full mt-8" data-id="element-2207">
          <Skeleton className="h-8 w-48 mb-6 bg-[#141414]" data-id="element-2208" />
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-8" data-id="element-2209">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="aspect-square rounded-2xl bg-[#141414]" data-id="element-2210" />)}
          </div>
          <div className="grid grid-cols-2 gap-3" data-id="element-2211">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="aspect-[4/5] rounded-2xl bg-[#141414]" data-id="element-2212" />)}
          </div>
        </div>
      </div>;
  }
  // ─── NOT FOUND ───
  if (!restaurant) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0B0B0B] p-4 text-[#F8F5F0]" data-id="element-2213">
        <EmptyState icon={Store} title="Restaurante no encontrado" description="El restaurante que buscas no existe o no está disponible." actionLabel="Volver al inicio" onAction={() => navigate('/')} data-id="element-2214" />
      </div>;
  }
  const cartCount = getItemCount();
  const cartTotal = getTotal();
  // Active category items
  const activeCategoryObj = categories.find(c => c.id === activeCategory);
  const activeCategoryItems = items.filter(item => item.category_id === activeCategory);
  // Upsell items
  const upsellItems = getUpsellItems();
  // Group upsell items by category for display
  const upsellGroups = categories.filter(c => isUpsellCategory(c.name)).map(c => ({
    category: c,
    items: items.filter(i => i.category_id === c.id)
  })).filter(g => g.items.length > 0);
  // Get featured items
  const featuredItems = items.filter((item: any) => item.featured === true || item.is_featured === true);
  // If not enough featured items, grab a few that have images
  const displayFeatured = featuredItems.length >= 3 ? featuredItems : [...featuredItems, ...items.filter(i => i.image_url && !featuredItems.find(f => f.id === i.id))].slice(0, 6);
  return <div className={cn('min-h-screen flex flex-col pb-28 relative', isCapricho ? 'bg-[#FBF7F2]' : 'bg-[#0B0B0B]')} data-id="element-2215">
      {/* ═══ BACKGROUND TEXTURE ═══ */}
      {isCapricho ? <div className="fixed inset-0 z-0 pointer-events-none" aria-hidden="true" data-id="element-2216">
          <div className="w-full h-full opacity-[0.04]" style={{
        backgroundImage: 'url(https://cdn.magicpatterns.com/uploads/qfQUG6xSwCibNddEpouoD3/ChatGPT_Image_21_mar_2026,_21_58_30.png)',
        backgroundSize: '320px',
        backgroundRepeat: 'repeat',
        backgroundPosition: 'center'
      }} data-id="element-2217" />
        </div> : <div className="fixed inset-0 z-0 pointer-events-none" data-id="element-2218">
          <img src="https://cdn.magicpatterns.com/uploads/6fAo78uf9tdtomvjsiMQAe/MX.jpg" alt="" className="w-full h-full object-cover opacity-[0.06]" loading="eager" aria-hidden="true" data-id="element-2219" />
        </div>}

      {/* ═══ HERO — RestaurantPage style ═══ */}
      <div className={cn('relative w-full overflow-hidden', isCapricho ? 'bg-[#FBF7F2]' : 'bg-[#0B0B0B]')} data-id="element-2220">
        {/* Cover Image */}
        <div className={cn('relative h-44 sm:h-56 md:h-64 w-full', isCapricho ? 'bg-[#F5E9DA]' : 'bg-[#141414]')} data-id="element-2221">
          {restaurant.cover_url || isCapricho ? <img src={isCapricho ? 'https://cdn.magicpatterns.com/uploads/wXcpDT288AkzVxuPArRvs7/ChatGPT_Image_21_mar_2026,_22_01_56.png' : restaurant.cover_url!} alt="Cover" className="absolute inset-0 w-full h-full object-cover" loading="eager" data-id="element-2222" /> : <div className="absolute inset-0 w-full h-full" style={{
          background: 'linear-gradient(160deg, #006847 0%, #004d35 40%, #0B0B0B 100%)'
        }} data-id="element-2223" />}
          <div className={cn('absolute inset-0 bg-gradient-to-t', isCapricho ? 'from-[#FBF7F2] via-[#FBF7F2]/40 to-transparent' : 'from-[#0B0B0B] via-black/40 to-transparent')} data-id="element-2224" />

          {/* Open/Closed Badge */}
          <div className="absolute top-4 right-4 z-10" data-id="element-2225">
            <div className={cn('flex items-center gap-1.5 px-3 py-1.5 rounded-full border', isCapricho ? 'bg-white/90 border-[#E8DDD0] shadow-sm' : 'bg-black/70 border-white/10')} data-id="element-2226">
              <div className={cn('w-2 h-2 rounded-full', restaurant.is_open ? 'bg-[#2ECC71]' : 'bg-[#CE1126]')} data-id="element-2227" />
              <span className={cn('text-xs font-medium', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-2228">
                {restaurant.is_open ? 'Abierto ahora' : 'Cerrado'}
              </span>
            </div>
          </div>
        </div>

        {/* Content overlay — RestaurantPage style */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 relative" data-id="element-2229">
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 sm:items-end -mt-10 sm:-mt-14 mb-4" data-id="element-2230">
            {/* Logo */}
            <div className={cn('relative rounded-2xl p-1 shadow-xl border shrink-0', isCapricho ? 'w-24 h-24 sm:w-32 sm:h-32 bg-[#FBF7F2] border-[#E8DDD0]' : 'w-20 h-20 sm:w-28 sm:h-28 bg-[#0B0B0B] border-white/10')} data-id="element-2231">
              {isCapricho ? <img src="https://cdn.magicpatterns.com/uploads/qfQUG6xSwCibNddEpouoD3/ChatGPT_Image_21_mar_2026,_21_58_30.png" alt={`${restaurant.name} logo`} className="w-full h-full rounded-xl object-contain" loading="eager" data-id="element-2232" /> : restaurant.logo_url ? <img src={restaurant.logo_url} alt={`${restaurant.name} logo`} className="w-full h-full rounded-xl object-cover" loading="eager" data-id="element-2233" /> : <img src="https://cdn.magicpatterns.com/uploads/66K7i7HxpjApVZJyeR9rjp/ChatGPT_Image_15_mar_2026,_13_53_22.png" alt="NOIZA" className="w-full h-full rounded-xl object-cover" data-id="element-2234" />}
            </div>

            {/* Title & Info */}
            <div className="flex-1 pb-2" data-id="element-2235">
              <h1 className={cn('text-2xl sm:text-4xl font-black mb-1 tracking-tight', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} style={isCapricho ? {
              fontFamily: "'Playfair Display', serif"
            } : undefined} data-id="element-2236">
                {restaurant.name}
              </h1>
              {restaurant.description_short && <p className={cn('text-sm sm:text-base max-w-2xl mb-3', isCapricho ? 'text-[#8B7355]' : 'text-white/50')} data-id="element-2237">
                  {restaurant.description_short}
                </p>}
            </div>
          </div>

          {/* Delivery badges + CTA */}
          <div className="flex flex-wrap items-center gap-2.5 pb-5" data-id="element-2238">
            {isCapricho ? <>
                {restaurantSettings?.accept_delivery !== false && <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold text-[#8B5E3C] bg-[#F5E9DA] border border-[#E8DDD0]" data-id="element-2239">
                    <Bike className="w-3.5 h-3.5" data-id="element-2240" /> Delivery
                  </span>}
                {restaurantSettings?.accept_pickup !== false && <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold text-[#8B5E3C] bg-[#F5E9DA] border border-[#E8DDD0]" data-id="element-2241">
                    <ShoppingBag className="w-3.5 h-3.5" data-id="element-2242" /> Para llevar
                  </span>}
              </> : <>
                {restaurantSettings?.accept_delivery !== false && <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold text-white border border-white/10" style={{
              background: 'linear-gradient(135deg, #006847 0%, #2AABB3 100%)'
            }} data-id="element-2243">
                    <Bike className="w-3.5 h-3.5" data-id="element-2244" /> Delivery
                  </span>}
                {restaurantSettings?.accept_pickup !== false && <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold text-white border border-white/10" style={{
              background: 'linear-gradient(135deg, #CE1126 0%, #E8751A 100%)'
            }} data-id="element-2245">
                    <ShoppingBag className="w-3.5 h-3.5" data-id="element-2246" /> Para llevar
                  </span>}
              </>}
            <button onClick={() => {
            const el = document.getElementById('menu-start');
            if (el) {
              el.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
              });
            }
          }} className={cn('ml-auto px-5 py-2 rounded-full text-xs sm:text-sm font-bold shadow-lg active:scale-95 transition-transform flex items-center gap-1.5', isCapricho ? 'bg-[#8B5E3C] text-white hover:bg-[#704A2E]' : 'bg-[#006847] text-white')} data-id="element-2247">
              Hacé tu pedido <Flame className="w-4 h-4" data-id="element-2248" />
            </button>
          </div>
        </div>
      </div>

      {categories.length === 0 ? <div className="relative z-10 max-w-4xl mx-auto w-full px-4 py-12" data-id="element-2249">
          <EmptyState icon={Store} title="Menú no disponible" description="Este restaurante aún no ha configurado su menú." data-id="element-2250" />
        </div> : <div id="menu-start" className="relative z-10 max-w-6xl mx-auto w-full flex flex-col sm:flex-row items-start gap-6 px-0 sm:px-6 pt-0 sm:pt-8" data-id="element-2251">
          {/* ═══ MENU NAVIGATION ═══ */}
          {/* Mobile: Sticky horizontal pills */}
          <div className={cn('sm:hidden sticky top-0 z-30 w-full border-b px-3 py-3 overflow-x-auto no-scrollbar flex items-center gap-2', isCapricho ? 'bg-[#FBF7F2] border-[#E8DDD0]' : 'bg-[#0B0B0B] border-white/5')} data-id="element-2252">
            {categories.map(category => {
          const isActive = activeCategory === category.id;
          return <button key={category.id} onClick={() => handleCategorySelect(category.id)} className={cn('whitespace-nowrap px-4 py-2 rounded-full text-xs font-bold transition-all flex items-center gap-1.5 border', isActive ? isCapricho ? 'bg-[#8B5E3C] text-white border-[#8B5E3C] shadow-md shadow-[#8B5E3C]/20' : 'bg-[#006847] text-white border-[#006847] shadow-lg shadow-[#006847]/20' : isCapricho ? 'bg-white text-[#8B7355] border-[#E8DDD0] hover:border-[#8B5E3C]/30' : 'bg-[#141414] text-white/70 border-white/10 hover:border-white/20')} data-id="element-2253">
                  <span className="text-sm" data-id="element-2254">
                    {getCategoryEmoji(category.name)}
                  </span>
                  {category.name}
                </button>;
        })}
          </div>

          {/* Desktop: Sticky left sidebar */}
          <div className={cn('hidden sm:flex sticky top-24 w-64 shrink-0 flex-col gap-1 pr-6 border-r max-h-[calc(100vh-8rem)] overflow-y-auto no-scrollbar', isCapricho ? 'border-[#E8DDD0]' : 'border-white/5')} data-id="element-2255">
            <h3 className={cn('text-xs font-black uppercase tracking-widest mb-3 px-3', isCapricho ? 'text-[#8B7355]/60' : 'text-white/40')} data-id="element-2256">
              Menú
            </h3>
            {categories.map(category => {
          const isActive = activeCategory === category.id;
          return <button key={category.id} onClick={() => handleCategorySelect(category.id)} className={cn('w-full text-left px-3 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center gap-3', isActive ? isCapricho ? 'bg-[#F5E9DA] text-[#8B5E3C]' : 'bg-[#006847]/10 text-[#F4D35E]' : isCapricho ? 'text-[#8B7355] hover:bg-white hover:text-[#3D2B1F]' : 'text-white/70 hover:bg-white/5 hover:text-white')} data-id="element-2257">
                  <span className="text-lg" data-id="element-2258">
                    {getCategoryEmoji(category.name)}
                  </span>
                  {category.name}
                </button>;
        })}
          </div>

          {/* ═══ MAIN CONTENT AREA ═══ */}
          <div className="flex-1 w-full px-3 sm:px-0 pb-12" data-id="element-2259">
            {/* FEATURED SECTION */}
            {displayFeatured.length > 0 && <section className="mb-10 sm:mb-14 pt-4 sm:pt-0" data-id="element-2260">
                <div className="flex items-center gap-2 mb-4 sm:mb-6 px-1" data-id="element-2261">
                  <span className="text-2xl" data-id="element-2262">⭐</span>
                  <h2 className={cn('text-xl sm:text-2xl font-black tracking-tight', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} style={isCapricho ? {
              fontFamily: "'Playfair Display', serif"
            } : undefined} data-id="element-2263">
                    Destacados de la casa
                  </h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-5" data-id="element-2264">
                  {displayFeatured.map((item: any) => <div key={`featured-${item.id}`} data-id="element-2265">
                      <MenuItemCard item={item} onClick={handleProductClick} theme={isCapricho ? 'capricho' : 'default'} data-id="element-2266" />
                    </div>)}
                </div>
              </section>}

            {/* FULL MENU BY CATEGORY */}
            <div className="flex flex-col gap-10 sm:gap-14" data-id="element-2267">
              {categories.map(category => {
            const categoryItems = items.filter(item => item.category_id === category.id);
            if (categoryItems.length === 0) return null;
            return <section key={category.id} id={`category-${category.id}`} className="scroll-mt-24 sm:scroll-mt-8" data-id="element-2268">
                    <div className={cn('flex items-center justify-between mb-4 sm:mb-6 px-1 border-b pb-3', isCapricho ? 'border-[#E8DDD0]' : 'border-white/5')} data-id="element-2269">
                      <h2 className={cn('text-xl sm:text-2xl font-black tracking-tight', isCapricho ? 'text-[#8B5E3C]' : 'text-[#F4D35E]')} style={isCapricho ? {
                  fontFamily: "'Playfair Display', serif"
                } : undefined} data-id="element-2270">
                        {getCreativeTitle(category.name)}
                      </h2>
                      <span className={cn('text-xs font-bold px-2.5 py-1 rounded-full', isCapricho ? 'text-[#8B7355] bg-white border border-[#E8DDD0]' : 'text-white/30 bg-white/5')} data-id="element-2271">
                        {categoryItems.length}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-5" data-id="element-2272">
                      {categoryItems.map((item, idx) => <div key={item.id} data-id="element-2273">
                          <MenuItemCard item={item} onClick={handleProductClick} theme={isCapricho ? 'capricho' : 'default'} data-id="element-2274" />
                        </div>)}
                    </div>
                  </section>;
          })}
            </div>
          </div>
        </div>}

      {/* ═══ FLOATING CART BUTTON ═══ */}
      {cartCount > 0 && !isCartOpen && <div className="fixed bottom-4 sm:bottom-6 left-0 right-0 z-40 px-3 sm:px-4 pointer-events-none" style={{
      paddingBottom: 'env(safe-area-inset-bottom, 0px)'
    }} data-id="element-2275">
          <div className="max-w-4xl mx-auto flex justify-center sm:justify-end" data-id="element-2276">
            <button onClick={() => setIsCartOpen(true)} className={cn('pointer-events-auto flex items-center justify-between w-full sm:w-auto sm:min-w-[320px] h-12 sm:h-14 px-5 sm:px-6 rounded-full text-white shadow-lg active:scale-95 transition-transform', isCapricho ? 'bg-[#8B5E3C] shadow-[#8B5E3C]/20 hover:bg-[#704A2E]' : 'bg-[#006847]')} data-id="element-2277">
              <div className="flex items-center gap-3" data-id="element-2278">
                <div className={cn('w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm', isCapricho ? 'bg-[#F5E9DA] text-[#8B5E3C]' : 'bg-[#F4D35E] text-[#0B0B0B]')} data-id="element-2279">
                  {cartCount}
                </div>
                <span className="font-bold text-[15px]" data-id="element-2280">Ver pedido</span>
              </div>
              <span className="font-bold text-[15px]" data-id="element-2281">
                {formatCurrency(cartTotal)}
              </span>
            </button>
          </div>
        </div>}

      {/* ═══ CART & CHECKOUT DRAWER ═══ */}
      {isCartOpen && <div className="fixed inset-0 z-50 flex justify-end" data-id="element-2282">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm anim-fade-in" onClick={() => setIsCartOpen(false)} data-id="element-2283" />
          <div className={cn('relative w-full sm:w-[480px] h-full shadow-2xl flex flex-col anim-slide-in-right', isCapricho ? 'bg-white' : 'bg-[#0B0B0B] border-l border-white/10')} data-id="element-2284">
            {/* Header */}
            <div className={cn('flex items-center justify-between px-6 py-5 border-b shrink-0', isCapricho ? 'bg-[#FBF7F2] border-[#E8DDD0]' : 'bg-[#0B0B0B] border-white/10')} data-id="element-2285">
              <div className="flex items-center gap-3" data-id="element-2286">
                {cartStep !== 'review' && cartStep !== 'success' && <button onClick={() => setCartStep('review')} className={cn('p-2 -ml-2 rounded-full transition-colors', isCapricho ? 'hover:bg-white text-[#8B7355]' : 'hover:bg-white/10 text-white')} data-id="element-2287">
                    <ArrowLeft className="w-5 h-5" data-id="element-2288" />
                  </button>}
                <h2 className={cn('text-xl font-bold', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} style={isCapricho ? {
              fontFamily: "'Playfair Display', serif"
            } : undefined} data-id="element-2289">
                  {cartStep === 'review' ? 'Tu Pedido' : cartStep === 'checkout' ? 'Finalizar Pedido' : '¡Pedido Confirmado!'}
                </h2>
              </div>
              <button onClick={() => setIsCartOpen(false)} className={cn('p-2 -mr-2 rounded-full transition-colors', isCapricho ? 'hover:bg-white text-[#8B7355]' : 'hover:bg-white/10 text-white')} data-id="element-2290">
                <X className="w-6 h-6" data-id="element-2291" />
              </button>
            </div>

            {/* Step: Review */}
            {cartStep === 'review' && <>
                <div className="flex-1 overflow-y-auto p-6" data-id="element-2292">
                  {cart.items.length === 0 ? <div className="h-full flex flex-col items-center justify-center text-center" data-id="element-2293">
                      <div className={cn('w-20 h-20 rounded-full flex items-center justify-center mb-6', isCapricho ? 'bg-[#F5E9DA]' : 'bg-[#141414]')} data-id="element-2294">
                        <ShoppingBag className={cn('w-8 h-8', isCapricho ? 'text-[#8B5E3C]' : 'text-white/20')} data-id="element-2295" />
                      </div>
                      <p className={cn('text-lg font-bold mb-2', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-2296">
                        Tu carrito está vacío
                      </p>
                      <p className={cn('text-sm', isCapricho ? 'text-[#8B7355]' : 'text-white/50')} data-id="element-2297">
                        Agrega algunos productos deliciosos para comenzar.
                      </p>
                      <button onClick={() => setIsCartOpen(false)} className={cn('mt-8 px-6 py-3 rounded-xl text-sm font-bold transition-colors', isCapricho ? 'bg-[#FBF7F2] text-[#8B5E3C] border border-[#E8DDD0] hover:bg-[#F5E9DA]' : 'bg-white/10 text-white hover:bg-white/20')} data-id="element-2298">
                        Ver menú
                      </button>
                    </div> : <div className="space-y-4" data-id="element-2299">
                      {cart.items.map(item => <CartItemComponent key={item.id} item={item} onUpdateQuantity={updateQuantity} onRemove={removeItem} theme={isCapricho ? 'capricho' : 'default'} data-id="element-2300" />)}
                    </div>}
                </div>

                {cart.items.length > 0 && <div className={cn('p-6 border-t shrink-0', isCapricho ? 'bg-[#FBF7F2] border-[#E8DDD0]' : 'bg-[#0B0B0B] border-white/10')} data-id="element-2301">
                    <div className="flex justify-between items-center mb-6" data-id="element-2302">
                      <span className={cn('font-medium', isCapricho ? 'text-[#8B7355]' : 'text-white/60')} data-id="element-2303">
                        Subtotal
                      </span>
                      <span className={cn('text-xl font-bold', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-2304">
                        {formatCurrency(cartTotal)}
                      </span>
                    </div>
                    <button onClick={() => setCartStep('checkout')} className={cn('w-full h-14 rounded-xl text-[15px] font-bold shadow-lg transition-colors', isCapricho ? 'bg-[#8B5E3C] text-white hover:bg-[#704A2E] shadow-[#8B5E3C]/20' : 'bg-[#006847] text-white active:bg-[#005a3d]')} data-id="element-2305">
                      Continuar al Checkout
                    </button>
                    <button onClick={() => {
              if (confirm('¿Estás seguro de vaciar el carrito?')) clearCart();
            }} className={cn('w-full text-center text-sm font-medium transition-colors py-3 mt-2', isCapricho ? 'text-[#8B7355] hover:text-[#CE1126]' : 'text-white/30 hover:text-[#CE1126]')} data-id="element-2306">
                      Vaciar carrito
                    </button>
                  </div>}
              </>}

            {/* Step: Checkout */}
            {cartStep === 'checkout' && <>
                <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6" data-id="element-2307">
                  <div className="space-y-4" data-id="element-2308">
                    <div className="space-y-1.5" data-id="element-2309">
                      <label className={cn('block text-sm font-semibold', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-2310">
                        Tu nombre
                      </label>
                      <input type="text" placeholder="Ej: María López" value={customerName} onChange={e => setCustomerName(e.target.value)} required className={cn('w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:border-transparent', isCapricho ? 'bg-[#FBF7F2] border-[#E8DDD0] text-[#3D2B1F] placeholder-[#8B7355]/50 focus:ring-[#8B5E3C]' : 'bg-[#141414] border-white/10 text-white placeholder-white/30 focus:ring-[#006847]')} data-id="element-2311" />
                    </div>
                    <div className="space-y-1.5" data-id="element-2312">
                      <label className={cn('block text-sm font-semibold', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-2313">
                        Tu teléfono
                      </label>
                      <input type="tel" placeholder="Ej: 8888-8888" value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} required className={cn('w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:border-transparent', isCapricho ? 'bg-[#FBF7F2] border-[#E8DDD0] text-[#3D2B1F] placeholder-[#8B7355]/50 focus:ring-[#8B5E3C]' : 'bg-[#141414] border-white/10 text-white placeholder-white/30 focus:ring-[#006847]')} data-id="element-2314" />
                    </div>
                  </div>

                  {/* Preorder Schedule Banner */}
                  {isPreorderRestaurant(restaurant?.id) && (() => {
              const windowInfo = getOrderingWindowMessage();
              const deliveryDates = isCapricho ? getUpcomingDeliveryDates(6) : getUpcomingSaturdays(3);
              return <div className="space-y-4" data-id="element-2315">
                          {/* Schedule Info Banner */}
                          <div className={cn('rounded-xl p-4 border', windowInfo.isOpen ? isCapricho ? 'bg-[#F5E9DA] border-[#E8DDD0]' : 'bg-[#006847]/10 border-[#006847]/30' : 'bg-[#CE1126]/10 border-[#CE1126]/30')} data-id="element-2316">
                            <p className={cn('font-bold text-sm mb-1', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-2317">
                              {windowInfo.title}
                            </p>
                            <p className={cn('text-xs leading-relaxed', isCapricho ? 'text-[#8B7355]' : 'text-white/60')} data-id="element-2318">
                              {windowInfo.message}
                            </p>
                            <div className={cn('mt-3 flex items-center gap-2 text-xs', isCapricho ? 'text-[#8B7355]/70' : 'text-white/40')} data-id="element-2319">
                              <span data-id="element-2320">📅 Pedidos: Lun–Vie</span>
                              <span data-id="element-2321">·</span>
                              <span data-id="element-2322">🚚 Entregas: Sáb y Dom 8am–4pm</span>
                            </div>
                          </div>

                          {/* Delivery Date Selection */}
                          <div data-id="element-2323">
                            <label className={cn('block text-sm font-semibold mb-3', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-2324">
                              {isCapricho ? 'Fecha de entrega' : 'Fecha de entrega (sábado)'}
                            </label>
                            <div className="space-y-2" data-id="element-2325">
                              {deliveryDates.map(d => {
                      const dateStr = toDateString(d);
                      const isSelected = preorderDate === dateStr;
                      return <button key={dateStr} type="button" onClick={() => handlePreorderDateSelect(dateStr)} className={cn('w-full p-3.5 rounded-xl border-2 text-sm font-semibold transition-all text-left', isSelected ? isCapricho ? 'border-[#8B5E3C] bg-[#F5E9DA] text-[#3D2B1F]' : 'border-[#F4D35E] bg-[#F4D35E]/10 text-white' : isCapricho ? 'border-[#E8DDD0] bg-white text-[#8B7355] hover:border-[#8B5E3C]/30' : 'border-white/10 bg-[#141414] text-white/60 hover:border-white/20')} data-id="element-2326">
                                    📅 {formatDeliveryDate(d)}
                                  </button>;
                    })}
                            </div>
                          </div>

                          {/* Capricho: Delivery Hour Picker */}
                          {isCapricho && preorderDate && !preorderCapacityFull && !checkingCapacity && <div data-id="element-2327">
                                <label className="block text-sm font-semibold mb-3 text-[#3D2B1F]" data-id="element-2328">
                                  Hora de entrega
                                </label>
                                <div className="grid grid-cols-3 gap-2" data-id="element-2329">
                                  {CAPRICHO_DELIVERY_HOURS.map(hour => {
                      const isSelected = deliveryHour === hour;
                      return <button key={hour} type="button" onClick={() => setDeliveryHour(hour)} className={cn('p-3 rounded-xl border-2 text-xs font-semibold transition-all', isSelected ? 'border-[#8B5E3C] bg-[#F5E9DA] text-[#3D2B1F]' : 'border-[#E8DDD0] bg-white text-[#8B7355] hover:border-[#8B5E3C]/30')} data-id="element-2330">
                                        🕐 {hour}
                                      </button>;
                    })}
                                </div>
                              </div>}

                          {/* Capacity Check Status */}
                          {preorderDate && checkingCapacity && <div className={cn('flex items-center gap-2 p-3.5 rounded-xl border text-sm', isCapricho ? 'bg-[#FBF7F2] border-[#E8DDD0] text-[#8B7355]' : 'bg-[#141414] border-white/10 text-white/60')} data-id="element-2331">
                              <svg className="animate-spin w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" data-id="element-2332">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-2333" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" data-id="element-2334" />
                              </svg>
                              Verificando disponibilidad...
                            </div>}

                          {/* Capacity Full Message */}
                          {preorderDate && preorderCapacityFull && !checkingCapacity && <div className={cn('p-4 rounded-xl border text-sm', isCapricho ? 'bg-[#F5E9DA] border-[#E8DDD0]' : 'bg-[#141414] border-white/10')} data-id="element-2335">
                                <p className={cn('font-semibold mb-1', isCapricho ? 'text-[#8B5E3C]' : 'text-[#F4D35E]')} data-id="element-2336">
                                  ✨ {CAPACITY_FULL_MESSAGE}
                                </p>
                              </div>}

                          {/* Time Slot Selection — Dynamic from Supabase */}
                          {preorderDate && !preorderCapacityFull && !checkingCapacity && <div data-id="element-2337">
                                <label className={cn('block text-sm font-semibold mb-3', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-2338">
                                  Horario de entrega
                                </label>
                                {timeSlotsLoading ? <div className={cn('flex items-center gap-2 p-3.5 rounded-xl border text-sm', isCapricho ? 'bg-[#FBF7F2] border-[#E8DDD0] text-[#8B7355]' : 'bg-[#141414] border-white/10 text-white/60')} data-id="element-2339">
                                    <svg className="animate-spin w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" data-id="element-2340">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-2341" />
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" data-id="element-2342" />
                                    </svg>
                                    Cargando horarios...
                                  </div> : <div className="grid grid-cols-2 gap-2" data-id="element-2343">
                                    {dynamicTimeSlots.map(slot => {
                      const isSelected = preorderTimeSlot === slot.label;
                      const isFull = !slot.available;
                      return <button key={slot.id} type="button" disabled={isFull} onClick={() => setPreorderTimeSlot(slot.label)} className={cn('p-3 rounded-xl border-2 text-xs font-semibold transition-all relative', isFull ? isCapricho ? 'border-[#E8DDD0] bg-[#FBF7F2] text-[#8B7355]/40 cursor-not-allowed' : 'border-white/5 bg-[#141414]/50 text-white/20 cursor-not-allowed' : isSelected ? isCapricho ? 'border-[#8B5E3C] bg-[#F5E9DA] text-[#3D2B1F]' : 'border-[#F4D35E] bg-[#F4D35E]/10 text-white' : isCapricho ? 'border-[#E8DDD0] bg-white text-[#8B7355] hover:border-[#8B5E3C]/30' : 'border-white/10 bg-[#141414] text-white/60 hover:border-white/20')} data-id="element-2344">
                                          🕐 {slot.label}
                                          {isFull && <span className={cn('block text-[10px] mt-0.5 font-medium', isCapricho ? 'text-[#8B7355]/50' : 'text-white/20')} data-id="element-2345">
                                              No disponible
                                            </span>}
                                        </button>;
                    })}
                                  </div>}
                              </div>}
                        </div>;
            })()}

                  <div data-id="element-2346">
                    <label className={cn('block text-sm font-semibold mb-3', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-2347">
                      Tipo de entrega
                    </label>
                    <div className={cn('grid gap-3', restaurantSettings?.accept_pickup !== false && restaurantSettings?.accept_delivery !== false ? 'grid-cols-2' : 'grid-cols-1')} data-id="element-2348">
                      {restaurantSettings?.accept_pickup !== false && <button type="button" onClick={() => setDeliveryType('pickup')} className={cn('p-4 rounded-xl border-2 text-sm font-semibold transition-all flex flex-col items-center justify-center gap-2', deliveryType === 'pickup' ? isCapricho ? 'border-[#8B5E3C] bg-[#8B5E3C] text-white shadow-md shadow-[#8B5E3C]/20' : 'border-[#006847] bg-[#006847] text-white shadow-md shadow-[#006847]/20' : isCapricho ? 'border-[#E8DDD0] bg-white text-[#8B7355] hover:border-[#8B5E3C]/30' : 'border-white/10 bg-[#141414] text-white/60 hover:border-white/20')} data-id="element-2349">
                          <Store className="w-5 h-5" data-id="element-2350" />
                          {isPreorderRestaurant(restaurant?.id) ? 'Recoger sábado' : 'Para llevar'}
                        </button>}
                      {restaurantSettings?.accept_delivery !== false && <button type="button" onClick={() => setDeliveryType('delivery')} className={cn('p-4 rounded-xl border-2 text-sm font-semibold transition-all flex flex-col items-center justify-center gap-2', deliveryType === 'delivery' ? isCapricho ? 'border-[#8B5E3C] bg-[#8B5E3C] text-white shadow-md shadow-[#8B5E3C]/20' : 'border-[#006847] bg-[#006847] text-white shadow-md shadow-[#006847]/20' : isCapricho ? 'border-[#E8DDD0] bg-white text-[#8B7355] hover:border-[#8B5E3C]/30' : 'border-white/10 bg-[#141414] text-white/60 hover:border-white/20')} data-id="element-2351">
                          <Bike className="w-5 h-5" data-id="element-2352" />
                          {isPreorderRestaurant(restaurant?.id) ? 'Delivery sábado' : 'Delivery'}
                        </button>}
                    </div>
                  </div>

                  {deliveryType === 'delivery' && <div className={cn('space-y-4 p-4 rounded-xl border', isCapricho ? 'bg-[#FBF7F2] border-[#E8DDD0]' : 'bg-[#141414] border-white/10')} data-id="element-2353">
                      <label className={cn('block text-sm font-semibold', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-2354">
                        Ubicación de entrega
                      </label>
                      <button type="button" onClick={handleGetLocation} disabled={isLocating} className={cn('w-full flex items-center justify-center gap-2 p-3.5 rounded-xl border-2 text-sm font-semibold transition-all', mapsUrl ? isCapricho ? 'border-[#8B5E3C]/30 bg-[#F5E9DA] text-[#8B5E3C]' : 'border-[#006847]/30 bg-[#006847]/10 text-[#2ECC71]' : isCapricho ? 'border-[#E8DDD0] bg-white text-[#3D2B1F] hover:border-[#8B5E3C]/30 shadow-sm' : 'border-white/10 bg-[#141414] text-white hover:border-white/20 shadow-sm')} data-id="element-2355">
                        {isLocating ? <>
                            <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none" data-id="element-2356">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-2357" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" data-id="element-2358" />
                            </svg>
                            Obteniendo ubicación...
                          </> : mapsUrl ? <>
                            <CheckCircle2 className="w-5 h-5" data-id="element-2359" />
                            Ubicación capturada
                          </> : <>
                            <MapPin className="w-5 h-5" data-id="element-2360" />
                            Compartir mi ubicación
                          </>}
                      </button>
                      {mapsUrl && <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className={cn('block text-xs underline underline-offset-2 truncate px-1', isCapricho ? 'text-[#C8A96A]' : 'text-[#F4D35E]')} data-id="element-2361">
                          {mapsUrl}
                        </a>}
                      {locationError && <p className={cn('text-sm border rounded-lg px-3 py-2.5', isCapricho ? 'text-[#CE1126] bg-[#CE1126]/5 border-[#CE1126]/20' : 'text-[#CE1126] bg-[#CE1126]/10 border-[#CE1126]/20')} data-id="element-2362">
                          {locationError}
                        </p>}
                      <div className="pt-2 space-y-1.5" data-id="element-2363">
                        <label className={cn('block text-sm font-semibold', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-2364">
                          Dirección exacta
                        </label>
                        <input type="text" placeholder="Ej: 200m norte del parque central, casa azul" value={deliveryAddress} onChange={e => setDeliveryAddress(e.target.value)} className={cn('w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:border-transparent', isCapricho ? 'bg-white border-[#E8DDD0] text-[#3D2B1F] placeholder-[#8B7355]/50 focus:ring-[#8B5E3C]' : 'bg-[#141414] border-white/10 text-white placeholder-white/30 focus:ring-[#006847]')} data-id="element-2365" />
                      </div>
                    </div>}

                  <div data-id="element-2366">
                    <label className={cn('block text-sm font-semibold mb-2', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-2367">
                      Notas (opcional)
                    </label>
                    <textarea value={orderNotes} onChange={e => setOrderNotes(e.target.value)} placeholder="Instrucciones especiales..." rows={3} className={cn('w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:border-transparent resize-none', isCapricho ? 'bg-[#FBF7F2] border-[#E8DDD0] text-[#3D2B1F] placeholder-[#8B7355]/50 focus:ring-[#8B5E3C]' : 'bg-[#141414] border-white/10 text-white placeholder-white/30 focus:ring-[#006847]')} data-id="element-2368" />
                  </div>

                  {/* Order Summary */}
                  <div className={cn('rounded-xl p-5 space-y-3 text-sm border', isCapricho ? 'bg-[#FBF7F2] border-[#E8DDD0]' : 'bg-[#141414] border-white/10')} data-id="element-2369">
                    <h4 className={cn('font-bold mb-3 text-base', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-2370">
                      Resumen del pedido
                    </h4>
                    {cart.items.map(item => {
                const optTotal = item.selected_options.reduce((s, o) => s + o.price_delta, 0);
                const lineTotal = (item.unit_price_snapshot + optTotal) * item.quantity;
                return <div key={item.id} className={cn('flex justify-between', isCapricho ? 'text-[#8B7355]' : 'text-white/70')} data-id="element-2371">
                          <span className="pr-4" data-id="element-2372">
                            <span className={cn('font-semibold', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-2373">
                              {item.quantity}x
                            </span>{' '}
                            {item.item_name_snapshot}
                          </span>
                          <span className={cn('font-medium shrink-0', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-2374">
                            {formatCurrency(lineTotal)}
                          </span>
                        </div>;
              })}
                    {deliveryType === 'delivery' && restaurant.express_fee > 0 && <div className={cn('flex justify-between pt-3 border-t mt-3', isCapricho ? 'text-[#8B7355] border-[#E8DDD0]' : 'text-white/70 border-white/10')} data-id="element-2375">
                          <span data-id="element-2376">Envío</span>
                          <span className={cn('font-medium', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-2377">
                            {formatCurrency(restaurant.express_fee)}
                          </span>
                        </div>}
                    <div className={cn('flex justify-between font-bold pt-3 border-t text-base mt-3', isCapricho ? 'text-[#3D2B1F] border-[#E8DDD0]' : 'text-white border-white/10')} data-id="element-2378">
                      <span data-id="element-2379">Total</span>
                      <span className={isCapricho ? 'text-[#C8A96A]' : 'text-[#F4D35E]'} data-id="element-2380">
                        {formatCurrency(cartTotal + (deliveryType === 'delivery' ? restaurant.express_fee || 0 : 0))}
                      </span>
                    </div>
                  </div>

                  {submitError && <div className={cn('border text-sm font-medium rounded-xl px-4 py-3 flex items-start gap-2', isCapricho ? 'bg-[#CE1126]/5 border-[#CE1126]/20 text-[#CE1126]' : 'bg-[#CE1126]/10 border-[#CE1126]/20 text-[#CE1126]')} data-id="element-2381">
                      <X className="w-5 h-5 shrink-0" data-id="element-2382" />
                      <p data-id="element-2383">{submitError}</p>
                    </div>}
                </div>

                <div className={cn('border-t p-6 shrink-0', isCapricho ? 'bg-white border-[#E8DDD0]' : 'bg-[#0B0B0B] border-white/10')} data-id="element-2384">
                  {isKitchenPaused && <div className="bg-red-900/40 border border-red-500/30 rounded-xl px-4 py-3 mb-3 text-center" data-id="element-2385">
                      <p className="text-red-300 text-sm font-medium" data-id="element-2386">
                        🔴 Cocina pausada — no se pueden enviar pedidos
                      </p>
                    </div>}
                  <button onClick={handleSubmitOrder} disabled={isSubmitting || isKitchenPaused || !customerName.trim() || !customerPhone.trim() || preorderCapacityFull || checkingCapacity || isPreorderRestaurant(restaurant?.id) && (!preorderDate || !preorderTimeSlot)} className={cn('w-full h-14 rounded-xl text-[15px] font-bold flex items-center justify-center transition-all', isSubmitting || isKitchenPaused || !customerName.trim() || !customerPhone.trim() || preorderCapacityFull || checkingCapacity || isPreorderRestaurant(restaurant?.id) && (!preorderDate || !preorderTimeSlot) ? isCapricho ? 'bg-[#FBF7F2] text-[#8B7355]/50 border border-[#E8DDD0] cursor-not-allowed' : 'bg-[#141414] text-white/30 cursor-not-allowed' : isCapricho ? 'bg-[#8B5E3C] hover:bg-[#704A2E] text-white shadow-lg shadow-[#8B5E3C]/20' : 'bg-[#25D366] hover:bg-[#20bd5a] text-white shadow-lg shadow-[#25D366]/30')} data-id="element-2387">
                    {isSubmitting ? <span className="flex items-center" data-id="element-2388">
                        <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" data-id="element-2389">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-2390" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" data-id="element-2391" />
                        </svg>
                        Procesando...
                      </span> : <>
                        <MessageCircle className="w-5 h-5 mr-2" data-id="element-2392" />
                        {isPreorderRestaurant(restaurant?.id) ? 'Confirmar Preorden por WhatsApp' : 'Confirmar y enviar por WhatsApp'}
                      </>}
                  </button>
                </div>
              </>}

            {/* Step: Success */}
            {cartStep === 'success' && completedOrder && <div className="flex-1 overflow-y-auto p-6 flex flex-col items-center justify-center text-center" data-id="element-2393">
                <div className={cn('w-20 h-20 rounded-full flex items-center justify-center mb-6', isCapricho ? 'bg-[#F5E9DA]' : 'bg-[#2ECC71]/10')} data-id="element-2394">
                  <CheckCircle2 className={cn('w-10 h-10', isCapricho ? 'text-[#8B5E3C]' : 'text-[#2ECC71]')} data-id="element-2395" />
                </div>
                <h3 className={cn('text-2xl font-bold mb-2', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} style={isCapricho ? {
            fontFamily: "'Playfair Display', serif"
          } : undefined} data-id="element-2396">
                  ¡Pedido registrado!
                </h3>
                <p className={cn('mb-8', isCapricho ? 'text-[#8B7355]' : 'text-white/60')} data-id="element-2397">
                  Tu número de orden es{' '}
                  <strong className={isCapricho ? 'text-[#3D2B1F]' : 'text-white'} data-id="element-2398">
                    #{completedOrder.orderNumber}
                  </strong>
                </p>

                <div className={cn('w-full p-5 rounded-2xl border mb-8', isCapricho ? 'bg-[#FBF7F2] border-[#E8DDD0]' : 'bg-[#141414] border-white/10')} data-id="element-2399">
                  <p className={cn('text-sm mb-4', isCapricho ? 'text-[#8B7355]' : 'text-white/70')} data-id="element-2400">
                    Para confirmar tu pedido y coordinar el pago, envíanos un
                    mensaje por WhatsApp.
                  </p>
                  <a href={completedOrder.whatsappUrl} target="_blank" rel="noopener noreferrer" className={cn('w-full h-12 rounded-xl text-[15px] font-bold flex items-center justify-center transition-all', isCapricho ? 'bg-[#8B5E3C] hover:bg-[#704A2E] text-white shadow-lg shadow-[#8B5E3C]/20' : 'bg-[#25D366] hover:bg-[#20bd5a] text-white shadow-lg shadow-[#25D366]/30')} data-id="element-2401">
                    <MessageCircle className="w-5 h-5 mr-2" data-id="element-2402" />
                    Enviar WhatsApp
                  </a>
                </div>

                <button onClick={() => {
            setIsCartOpen(false);
            clearCart();
          }} className={cn('text-sm font-bold transition-colors', isCapricho ? 'text-[#8B7355] hover:text-[#3D2B1F]' : 'text-white/50 hover:text-white')} data-id="element-2403">
                  Cerrar y volver al menú
                </button>
              </div>}
          </div>
        </div>}

      {/* ═══ UPSELL MODAL ═══ */}
      {showUpsellModal && <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center" data-id="element-2404">
          <div className="fixed inset-0 bg-black/80" onClick={() => {
        setShowUpsellModal(false);
        setCartStep('checkout');
      }} data-id="element-2405" />
          <div className={cn('relative w-full sm:max-w-lg max-h-[85vh] sm:rounded-2xl rounded-t-2xl shadow-lg flex flex-col anim-slide-up border', isCapricho ? 'bg-white border-[#E8DDD0]' : 'bg-[#0B0B0B] border-white/10')} data-id="element-2406">
            <div className="px-6 pt-6 pb-4 shrink-0" data-id="element-2407">
              <div className="flex items-center justify-between mb-1" data-id="element-2408">
                <h3 className={cn('text-xl font-black', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} style={isCapricho ? {
              fontFamily: "'Playfair Display', serif"
            } : undefined} data-id="element-2409">
                  ¿Le agregamos algo más? 🤤
                </h3>
                <button onClick={() => {
              setShowUpsellModal(false);
              setCartStep('checkout');
            }} className={cn('p-1.5 transition-colors', isCapricho ? 'text-[#8B7355] hover:text-[#3D2B1F]' : 'text-white/40 hover:text-white')} data-id="element-2410">
                  <X className="w-5 h-5" data-id="element-2411" />
                </button>
              </div>
              <p className={cn('text-sm', isCapricho ? 'text-[#8B7355]' : 'text-white/50')} data-id="element-2412">
                Complementa tu pedido con estas opciones
              </p>
            </div>

            <div className="flex-1 overflow-y-auto px-6 pb-4" data-id="element-2413">
              {upsellGroups.map(group => <div key={group.category.id} className="mb-6 last:mb-2" data-id="element-2414">
                  <h4 className={cn('text-sm font-bold mb-3 uppercase tracking-wider', isCapricho ? 'text-[#8B5E3C]' : 'text-[#F4D35E]')} data-id="element-2415">
                    {getCategoryEmoji(group.category.name)}{' '}
                    {group.category.name}
                  </h4>
                  <div className="space-y-2" data-id="element-2416">
                    {group.items.map(item => <div key={item.id} className={cn('flex items-center gap-3 rounded-xl p-3 border', isCapricho ? 'bg-[#FBF7F2] border-[#E8DDD0]' : 'bg-[#141414] border-white/5')} data-id="element-2417">
                        {item.image_url ? <img src={item.image_url} alt={item.name} className="w-14 h-14 rounded-xl object-cover shrink-0" data-id="element-2418" /> : <div className={cn('w-14 h-14 rounded-xl flex items-center justify-center shrink-0', isCapricho ? 'bg-[#F5E9DA]' : 'bg-[#1a1a1a]')} data-id="element-2419">
                            <span className="text-xl opacity-30" data-id="element-2420">
                              {getCategoryEmoji(group.category.name)}
                            </span>
                          </div>}
                        <div className="flex-1 min-w-0" data-id="element-2421">
                          <p className={cn('text-sm font-semibold truncate', isCapricho ? 'text-[#3D2B1F]' : 'text-white')} data-id="element-2422">
                            {item.name}
                          </p>
                          <p className={cn('text-sm font-bold', isCapricho ? 'text-[#C8A96A]' : 'text-[#F4D35E]')} data-id="element-2423">
                            {formatCurrency(item.price)}
                          </p>
                        </div>
                        <button onClick={() => handleQuickAdd(item)} className={cn('w-9 h-9 rounded-full flex items-center justify-center transition-colors shadow-md shrink-0 active:scale-90', isCapricho ? 'bg-[#8B5E3C] text-white hover:bg-[#704A2E] shadow-[#8B5E3C]/20' : 'bg-[#006847] text-white hover:bg-[#005a3d] shadow-[#006847]/20')} data-id="element-2424">
                          <Plus className="w-4 h-4" data-id="element-2425" />
                        </button>
                      </div>)}
                  </div>
                </div>)}
              {upsellGroups.length === 0 && <p className={cn('text-center py-8 text-sm', isCapricho ? 'text-[#8B7355]' : 'text-white/40')} data-id="element-2426">
                  No hay sugerencias disponibles
                </p>}
            </div>

            <div className={cn('px-6 pb-6 pt-3 shrink-0 border-t space-y-2.5', isCapricho ? 'border-[#E8DDD0]' : 'border-white/5')} data-id="element-2427">
              <button onClick={() => setShowUpsellModal(false)} className={cn('w-full h-12 rounded-xl border font-bold text-sm transition-colors', isCapricho ? 'bg-[#FBF7F2] border-[#E8DDD0] text-[#8B5E3C] hover:bg-[#F5E9DA]' : 'bg-[#141414] border-white/10 text-white hover:bg-[#1a1a1a]')} data-id="element-2428">
                Seguir agregando
              </button>
              <button onClick={() => {
            setShowUpsellModal(false);
            setCartStep('checkout');
          }} className={cn('w-full h-12 rounded-xl font-bold text-sm shadow-lg transition-colors', isCapricho ? 'bg-[#8B5E3C] text-white shadow-[#8B5E3C]/30 hover:bg-[#704A2E]' : 'bg-[#006847] text-white shadow-[#006847]/30 hover:bg-[#005a3d]')} data-id="element-2429">
                Continuar al pedido →
              </button>
            </div>
          </div>
        </div>}

      {/* ═══ PRODUCT DETAIL MODAL ═══ */}
      <ProductDetailModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} item={selectedProduct} onAdd={handleAddToCart} primaryColor={restaurant.primary_color || undefined} theme={isCapricho ? 'capricho' : 'default'} data-id="element-2430" />

      {/* ═══ CAPRICHO WELCOME MODAL ═══ */}
      {showCaprichoWelcome && isCapricho && <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-id="element-2431">
          <div className="relative w-full max-w-md rounded-2xl overflow-hidden shadow-2xl" style={{
        backgroundColor: '#FBF7F2'
      }} data-id="element-2432">
            {/* Header accent */}
            <div className="h-2 w-full" style={{
          background: 'linear-gradient(90deg, #8B5E3C, #C8A96A, #8B5E3C)'
        }} data-id="element-2433" />

            <div className="p-6 space-y-5" data-id="element-2434">
              {/* Logo + Title */}
              <div className="text-center" data-id="element-2435">
                <div className="w-16 h-16 mx-auto mb-3 rounded-xl overflow-hidden border-2 border-[#E8DDD0] bg-white" data-id="element-2436">
                  <img src="https://cdn.magicpatterns.com/uploads/qfQUG6xSwCibNddEpouoD3/ChatGPT_Image_21_mar_2026,_21_58_30.png" alt="Capricho de Canela" className="w-full h-full object-contain p-1" data-id="element-2437" />
                </div>
                <h2 className="text-xl font-bold text-[#3D2B1F]" style={{
              fontFamily: "'Playfair Display', serif"
            }} data-id="element-2438">
                  ¡Bienvenido a Capricho de Canela!
                </h2>
              </div>

              {/* Info items */}
              <div className="space-y-3" data-id="element-2439">
                <div className="flex items-start gap-3 p-3 rounded-xl bg-[#F5E9DA] border border-[#E8DDD0]" data-id="element-2440">
                  <span className="text-lg shrink-0" data-id="element-2441">📦</span>
                  <p className="text-sm text-[#3D2B1F] font-medium" data-id="element-2442">
                    Recibimos tus pedidos de <strong data-id="element-2443">Lunes a Viernes</strong>.
                  </p>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-xl bg-[#F5E9DA] border border-[#E8DDD0]" data-id="element-2444">
                  <span className="text-lg shrink-0" data-id="element-2445">🚚</span>
                  <p className="text-sm text-[#3D2B1F] font-medium" data-id="element-2446">
                    Entregamos <strong data-id="element-2447">Sábado y Domingo</strong>.
                  </p>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-xl bg-[#F5E9DA] border border-[#E8DDD0]" data-id="element-2448">
                  <span className="text-lg shrink-0" data-id="element-2449">📅</span>
                  <p className="text-sm text-[#3D2B1F] font-medium" data-id="element-2450">
                    Próxima fecha de entrega:{' '}
                    <strong data-id="element-2451">Sábado 28 de Marzo</strong>.
                  </p>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-xl bg-[#F5E9DA] border border-[#E8DDD0]" data-id="element-2452">
                  <span className="text-lg shrink-0" data-id="element-2453">😍</span>
                  <p className="text-sm text-[#3D2B1F] font-medium" data-id="element-2454">
                    ¡Estás listo para elegir tu capricho!
                  </p>
                </div>
              </div>

              {/* CTA Button */}
              <button onClick={() => {
            setShowCaprichoWelcome(false);
            sessionStorage.setItem('capricho-welcome-dismissed', 'true');
          }} className="w-full py-3.5 rounded-xl font-bold text-white text-sm transition-all hover:opacity-90 active:scale-[0.98]" style={{
            backgroundColor: '#8B5E3C'
          }} data-id="element-2455">
                🍪 ¡Quiero mi Capricho!
              </button>
            </div>
          </div>
        </div>}
    </div>;
}